import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import useForm, { FormContext } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { makeStyles } from '@material-ui/core/styles';
import {
    Button,
    Box,
    Typography
} from '@material-ui/core';
import { addComment, editComment } from '../../actions/threadActions';
import TextField from '../form/TextField';

const useStyles = makeStyles((theme) => ({
    formContainer: {
        position: 'relative',
        margin: 'auto',
        maxWidth: '60rem',
        [theme.breakpoints.down('sm')]: {
            width: '100%',
        },
    },
    formTitle: {
        fontSize: '1.6rem',
        fontWeight: 700,
        color: theme.palette.primary.main,
        marginBottom: theme.spacing(2),
    },
    textField: {
        marginBottom: theme.spacing(3),
    },
    buttonContainer: {
        display: 'flex',
        gap: theme.spacing(2),
        justifyContent: 'flex-start',
    },
    submitButton: {
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.primary.contrastText,
        fontSize: '1.4rem',
        fontWeight: 600,
        padding: theme.spacing(1, 3),
        '&:hover': {
            backgroundColor: theme.palette.primary.dark,
            transform: 'translateY(-1px)',
            boxShadow: theme.shadows[4],
        },
        transition: 'all 0.2s ease-in-out',
    },
    cancelButton: {
        backgroundColor: theme.palette.error.main,
        color: theme.palette.error.contrastText,
        fontSize: '1.4rem',
        fontWeight: 600,
        padding: theme.spacing(1, 3),
        '&:hover': {
            backgroundColor: theme.palette.error.dark,
            transform: 'translateY(-1px)',
            boxShadow: theme.shadows[4],
        },
        transition: 'all 0.2s ease-in-out',
    },
}));

const ThreadCommentForm = ({ editMode, onCancel, onUpdate, threadId }) => {
    const classes = useStyles();
    const dispatch = useDispatch();
    const selectedComment = useSelector(state => state.thread.selectedComment);
    const methods = useForm({ mode: 'onChange' });
    const { setValue, handleSubmit } = methods;

    useEffect(() => {
        if (editMode && selectedComment) {
            setValue('comment', selectedComment.comment);
        } else {
            setValue('comment', '');
        }
    }, [selectedComment, editMode, setValue]);

    const onSubmit = data => {
        if (editMode) {
            dispatch(editComment(threadId, selectedComment._id, data.comment));
        } else {
            dispatch(addComment(threadId, data.comment));
        }
        onUpdate();
    };

    return (
        <FormContext {...methods}>
            <Box className={classes.formContainer}>
                <form onSubmit={handleSubmit(onSubmit)}>
                    <TextField
                        label={editMode ? 'コメントを編集（1000字以内）' : 'コメントを追加（1000字以内）'}
                        type="textarea"
                        rows={12}
                        name="comment"
                        registerOptions={{
                            required: true,
                            maxLength: 1000,
                            mode: 'onChange'
                        }}
                    />

                    <Box className={classes.buttonContainer}>
                        <Button
                            type="submit"
                            variant="contained"
                            color="primary"
                            className={classes.submitButton}
                        >
                            {editMode ? '更新' : '投稿する'}
                        </Button>
                        <Button
                            type="button"
                            variant="contained"
                            color="secondary"
                            onClick={onCancel}
                            className={classes.cancelButton}
                        >
                            キャンセル
                        </Button>
                    </Box>
                </form>
            </Box>
        </FormContext>
    );
};

ThreadCommentForm.propTypes = {
    threadId: PropTypes.string.isRequired,
    editMode: PropTypes.bool,
    comment: PropTypes.object,
    onUpdate: PropTypes.func,
    onCancel: PropTypes.func
};

export default ThreadCommentForm;
